// The Index Sheet Datasheets tab of the Project Datasheet Composer: this database's Index Sheets,
// each with its datasheet and that datasheet's state against the template library, and the actions
// that create, assign, revise, refresh or bring in a sheet's datasheet. Every call is gated on
// utilities.ds.project.composer for this database.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Plus, Link2, GitBranch, Eye, Download, Pencil } from 'lucide-react';
import { dsProjectApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { applySearch, SEARCH_HINT } from '../../utils/listSearch';
import { linesToBoxes, toIndexSide } from './dsBoxes';
import { sheetLayout } from '../development/dsTemplateLayout';
import { useDsTemplates } from '../development/useDsTemplates';
import DsDiffViewerModal from './DsDiffViewerModal';
import { ConfirmModal } from './CatalogModals';
import { CreateDatasheetModal, AssignDatasheetModal, ReviseModal } from './ProjectSheetModals';
import ProjectLayoutEditor from './ProjectLayoutEditor';
import {
  stateBadgeClass, stateLabel, revOf, rowActions, FILTERS, filterRows, errText, layoutBlocker,
} from './projectSheets';

const SEARCH_FIELDS = ['indexSheet', 'description', 'type', 'manufacturerCode', 'dsIndex', 'state'];

export default function ProjectSheetsTab() {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };
  const specRows = useDsTemplates(dsProjectApi.getLayoutSpec);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [templates, setTemplates] = useState(null);   // Published ISA Templates, loaded on first Create
  const [dialog, setDialog] = useState(null);         // { kind, row, existing? }
  const [diff, setDiff] = useState(null);             // DsDiffViewerModal props + the row
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);       // the row whose layout is open in the editor

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dsProjectApi.getSheets();
      setRows(res.data ?? []);
    } catch (e) { notify.error(errText(e, 'Failed to load the Index Sheets.')); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterRows(rows, filter), [rows, filter]);
  const shown = useMemo(() => applySearch(filtered, search, SEARCH_FIELDS), [filtered, search]);

  // Runs one write, reports it, closes whatever dialog started it and reloads the list.
  const run = async (call, success, fallback) => {
    setBusy(true);
    try {
      const res = await call();
      notify.success(success(res?.data ?? {}));
      setDialog(null);
      setDiff(null);
      await load();
      return true;
    } catch (e) {
      notify.error(errText(e, fallback));
      return false;
    } finally { setBusy(false); }
  };

  const openCreate = async row => {
    if (templates === null) {
      try {
        const res = await dsProjectApi.getIsaTemplates();
        setTemplates(res.data ?? []);
      } catch (e) { notify.error(errText(e, 'Failed to load the ISA Templates.')); return; }
    }
    setDialog({ kind: 'create', row });
  };

  const handleCreate = async (row, templateId) => {
    setBusy(true);
    try {
      const res = await dsProjectApi.createDatasheet(row.id, templateId, auditCfg);
      notify.success(`${res.data.dsIndex} created and assigned to ${row.indexSheet}.`);
      setDialog(null);
      await load();
    } catch (e) {
      // The library already holds this form's copy for the code: offer it instead.
      if (e?.response?.status === 409 && e.response.data?.existingId != null) {
        setDialog({ kind: 'create', row, existing: e.response.data });
      } else notify.error(errText(e, 'Create failed.'));
    } finally { setBusy(false); }
  };

  const handleAssign = (row, dsIndexId) => run(
    () => dsProjectApi.assign(row.id, dsIndexId, auditCfg),
    d => `${row.indexSheet} now uses ${d.dsIndex}` +
      (d.broughtIntoProject ? ' (copied into this project)' : '') +
      (d.stale ? '. This project\'s copy differs from the library.' : '.'),
    'Assign failed.');

  const handleRevise = row => run(
    () => dsProjectApi.revise(row.id, auditCfg),
    d => `${d.dsIndex} created; ${row.indexSheet} moved to it with ${d.valuesMoved} value${d.valuesMoved === 1 ? '' : 's'}.` +
      (d.valuesLeftInPlace ? ` ${d.valuesLeftInPlace} value${d.valuesLeftInPlace === 1 ? '' : 's'} on other boxes stayed where they were.` : ''),
    'Revise failed.');

  const handleRefresh = async row => {
    setBusy(true);
    try {
      const res = await dsProjectApi.refresh(row.id, auditCfg);
      const d = res.data;
      notify.success(`${d.dsIndex}: ${d.boxesInserted} boxes added, ${d.boxesUpdated} updated, ${d.boxesDeleted} removed.`);
      if ((d.boxesKeptWithValues ?? []).length > 0)
        notify.warning(`${d.boxesKeptWithValues.length} box${d.boxesKeptWithValues.length === 1 ? '' : 'es'} only this project has were kept because values sit on them: ${d.boxesKeptWithValues.join(', ')}.`);
      setDialog(null);
      setDiff(null);
      await load();
    } catch (e) { notify.error(errText(e, 'Refresh failed.')); }
    finally { setBusy(false); }
  };

  // Library copy on the left, this project's on the right, the sheet's values on both so only
  // the layout differences light up.
  const openDiff = async row => {
    try {
      const { data } = await dsProjectApi.getDiff(row.id);
      const side = s => (s ? linesToBoxes(s.lines ?? []).map(toIndexSide) : []);
      const rec = data.project ?? data.library;
      const sheetCount = Math.max(1, rec?.sheetCount ?? 1);
      setDiff({
        row,
        title: `${row.indexSheet}: library vs this project`,
        leftLabel: data.library ? `Library (${data.library.dsIndex})` : 'Library (none)',
        rightLabel: data.project ? 'This project' : 'This project (none)',
        leftBoxes: side(data.library),
        rightBoxes: side(data.project),
        leftValues: data.values ?? {},
        rightValues: data.values ?? {},
        sheetCount,
        layout: sheetLayout(specRows, rec?.tempType ?? null, 1, sheetCount, rec?.dsName ?? null),
      });
    } catch (e) { notify.error(errText(e, 'Failed to load the datasheets.')); }
  };

  const actionButtons = row => rowActions(row).map(a => {
    switch (a) {
      case 'create':
        return <button key={a} className="stui-btn stui-btn-primary stui-btn-sm" onClick={() => openCreate(row)}><Plus size={14} /> Create</button>;
      case 'useExisting':
        return <button key={a} className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setDialog({ kind: 'assign', row })}><Link2 size={14} /> Use existing</button>;
      case 'assign':
        return <button key={a} className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setDialog({ kind: 'assign', row })} title="Assign another revision"><Link2 size={14} /></button>;
      case 'revise':
        return <button key={a} className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setDialog({ kind: 'revise', row })}><GitBranch size={14} /> Revise</button>;
      case 'editLayout': {
        const why = layoutBlocker(row);
        return <button key={a} className="stui-btn stui-btn-secondary stui-btn-sm" disabled={!!why} title={why || 'Edit the boxes of this datasheet revision'}
          onClick={() => setEditing(row)}><Pencil size={14} /> Edit layout</button>;
      }
      case 'diff':
        return <button key={a} className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => openDiff(row)}><Eye size={14} /> Difference</button>;
      case 'bringIn':
        return <button key={a} className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setDialog({ kind: 'bringIn', row })}><Download size={14} /> Bring in</button>;
      default:
        return null;
    }
  });

  if (editing) {
    return <ProjectLayoutEditor row={editing} onClose={changed => { setEditing(null); if (changed) load(); }} />;
  }

  return (
    <>
      <div className="stui-page-header">
        <div className="stui-row-8">
          <div className="stui-segmented">
            {FILTERS.map(([id, label]) => (
              <button key={id} className={`stui-segmented-btn${filter === id ? ' selected' : ''}`} onClick={() => setFilter(id)}>
                {`${label} (${filterRows(rows, id).length})`}
              </button>
            ))}
          </div>
          <span className="stui-text-sm-muted">{shown.length === rows.length ? `${rows.length}` : `${shown.length} of ${rows.length}`}</span>
        </div>
        <div className="stui-row-8">
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={search} onClear={() => setSearch('')} />
            <input className="stui-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." title={SEARCH_HINT} />
          </div>
          <button className="stui-btn stui-btn-secondary" onClick={load} disabled={loading} title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="stui-detail-fill stui-mt-sm">
        <div className="stui-data-grid-container">
          <div className="stui-data-grid-scroll-area">
            <table className="stui-data-grid stui-data-grid-auto">
              <thead>
                <tr><th>Index Sheet</th><th>Description</th><th>Type</th><th>Mfr</th><th>Datasheet</th><th>Rev</th><th>Values</th><th>State</th><th /></tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={9} className="stui-td-empty">
                    {loading ? 'Loading…' : rows.length === 0 ? 'This database has no Index Sheets.' : 'No matching results'}
                  </td></tr>
                )}
                {shown.map(r => (
                  <tr key={r.id}>
                    <td className="stui-mono">{r.indexSheet}</td>
                    <td>{r.description}</td>
                    <td>{r.type}</td>
                    <td title={r.manufacturer || ''}>{r.manufacturerCode}</td>
                    <td className="stui-mono">{r.dsIndex ?? ''}</td>
                    <td className="stui-mono">{revOf(r.dsIndex)}</td>
                    <td title={r.valuesOffDatasheet ? `${r.valuesOffDatasheet} more sit on boxes of another datasheet and do not print` : ''}>
                      {r.dsConfig != null ? r.valueCount : ''}
                      {r.valuesOffDatasheet ? <span className="stui-text-warning">{` +${r.valuesOffDatasheet}`}</span> : null}
                    </td>
                    <td title={r.stateNote || ''}>
                      <span className={`stui-badge ${stateBadgeClass(r.state)}`}>{stateLabel(r.state)}</span>
                    </td>
                    <td>
                      {r.conforming
                        ? <div className="stui-row-4">{actionButtons(r)}</div>
                        : <span className="stui-td-muted" title={r.blocker}>Rename to the code grammar first</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {dialog?.kind === 'create' && (
        <CreateDatasheetModal row={dialog.row} templates={templates ?? []} existing={dialog.existing ?? null} busy={busy}
          onCreate={id => handleCreate(dialog.row, id)}
          onUseExisting={id => handleAssign(dialog.row, id)}
          onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'assign' && (
        <AssignDatasheetModal row={dialog.row} busy={busy}
          onAssign={id => handleAssign(dialog.row, id)} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'revise' && (
        <ReviseModal row={dialog.row} busy={busy} onRevise={() => handleRevise(dialog.row)} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'bringIn' && (
        <ConfirmModal title={`Bring ${dialog.row.dsIndex} into this project`} confirmLabel="Bring in" busy={busy}
          onConfirm={() => handleRefresh(dialog.row)} onClose={() => setDialog(null)}>
          <p className="stui-hint">
            {`${dialog.row.indexSheet} points at ${dialog.row.dsIndex}, which the template library has and this project does not. ` +
              'It is copied in with the same IDs.'}
          </p>
        </ConfirmModal>
      )}
      {diff && (
        <DsDiffViewerModal
          {...diff}
          onClose={() => setDiff(null)}
          actions={[
            { label: 'Keep this project\'s', onClick: () => setDiff(null), disabled: busy },
            { label: busy ? 'Refreshing…' : 'Refresh from library', primary: true, disabled: busy, onClick: () => handleRefresh(diff.row) },
          ]}
        />
      )}
    </>
  );
}
